import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { MapModalData } from '../../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { UserAccessService } from '../../../../../../user/access/user-access.service';
import {
  generateInaccessibleModalData,
  signUpWouldNotUnlock,
} from './inacessible-features-util';
import { ModalService } from '../../../../../../shared/services/modal.service';
import { GoogleAnalyticsService } from '../../../../../../user/google-analytics.service';

/**
 * Rendered once per menu indicator - on the order of a hundred live instances
 * while the layers menu is open - so everything here is sized for that
 * multiplier rather than for a single icon:
 *
 * - OnPush: the template reads nothing but inputs, so a global tick (Mapbox
 *   fires them at frame rate) has no reason to walk this view at all.
 * - Discrete `[style.x]` bindings instead of `[ngStyle]` object literals,
 *   which were re-allocated every pass and forced a KeyValueDiffer run for
 *   colours that never change.
 * - `RouterLink` is instantiated imperatively: exactly one call site passes a
 *   link, and the directive subscribes to `router.events` per instance. On a
 *   non-anchor element it renders no href, so navigating from the click
 *   handler is equivalent.
 * - The modal payload is built on first click rather than in `ngOnInit`,
 *   which used to run a string split and allocate a long disclaimer per
 *   instance while the menu was being built.
 */
@Component({
  selector: 'app-feature-tooltip',
  templateUrl: './feature-tooltip.component.html',
  styleUrls: ['./feature-tooltip.component.scss'],
  imports: [NgIf, MatTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureTooltipComponent {
  @Input() tooltipText!: string;
  @Input() tooltipRouterLink!: string | null;
  @Input() featureName!: string | undefined;
  @Input() featureConst!: string;
  @Input() headerText!: string | undefined;
  @Input() fillColor: string = '#ccc';
  @Input() strokeColor: string = '#A3A3A3';
  @Input() opacity: number = 0.35;
  @Input() clickable: boolean = true;
  @Input() tooltipPosition = 'above' as TooltipPosition;
  @Input() isFeatureInaccessibleType: boolean = false;

  @Output() openTooltipModal = new EventEmitter<MapModalData>();

  private tooltipModalData!: MapModalData;

  constructor(
    private accessService: UserAccessService,
    private gaService: GoogleAnalyticsService,
    private modalService: ModalService,
    private router: Router
  ) {}

  public handleTooltipClick(event: Event): void {
    if (this.tooltipRouterLink) {
      // Deliberately no stopPropagation: this used to be a RouterLink, whose
      // click listener let the event keep bubbling to the group header.
      this.router.navigateByUrl(this.tooltipRouterLink);
      return;
    }

    event.stopPropagation();

    if (this.isFeatureInaccessibleType) {
      if (this.accessService.getIsUnauthorized()) {
        this.gaService.logLockedParamClickGuest();
      } else {
        this.gaService.logLockedParamClickRegistered();
      }
      // Sign-up cannot unlock a paywalled indicator, so the plans modal is
      // the honest answer - the same one the popup's full-access CTA opens.
      // It is opened here rather than emitted because it is a different
      // dialog, not a MapModalData the parent could pass on.
      if (signUpWouldNotUnlock(this.featureConst, this.accessService)) {
        this.modalService.openSubscriptionsModal('all_packages');
        return;
      }

      // If the feature is locked, generate specific modal data for upselling
      this.openTooltipModal.emit(this.getInaccessibleModalData());
    } else {
      // If the feature is accessible, show the default info modal
      if (!this.tooltipModalData) this.generateDefaultModalData();
      this.openTooltipModal.emit(this.tooltipModalData);
    }
  }

  private generateDefaultModalData(): void {
    const parts = this.tooltipText.split('Source:');
    const mainText = parts[0].trim();
    const source = 'Source: ' + parts.slice(1).join('Source: ').trim();

    this.tooltipModalData = {
      headerText: this.featureName,
      subHeaderText: mainText,
      mainText: source,
      disableClose: false,
      hasBackdrop: true,
      disclaimerText:
        'Data and related materials represent an estimation produced from the data provided by a third party. These data are not guaranteed to be accurate, up to date or complete, and are provided "as is". Map AI makes no representations or warranties expressed or implied, with respect to the methods, algorithms, software, data or the related materials, including by way of example and not limitation, the implied warranties of merchantability and fitness for a particular purpose.',
    };
  }

  private getInaccessibleModalData(): MapModalData {
    const featureDisplayName = this.featureName || 'This';
    const isUnauthorized = this.accessService.getIsUnauthorized();

    return generateInaccessibleModalData(featureDisplayName, isUnauthorized);
  }
}
